
import React, { useState, useEffect } from 'react';
import { Professional, ListingTier } from '../types';
import { Star, MapPin, Phone, Heart, ShieldCheck, ShieldAlert, Globe, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ListingCardProps {
  professional: Professional;
}

const ListingCard: React.FC<ListingCardProps> = ({ professional }) => {
  const isPremiumOrExclusive = professional.tier === ListingTier.PREMIUM || professional.tier === ListingTier.EXCLUSIVE;
  const isFeatured = professional.tier === ListingTier.FEATURED;
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('planstack_shortlist') || '[]');
    setIsSaved(saved.includes(professional.id));
  }, [professional.id]);

  const toggleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const saved = JSON.parse(localStorage.getItem('planstack_shortlist') || '[]');
    let updated;
    if (saved.includes(professional.id)) {
      updated = saved.filter((id: string) => id !== professional.id);
    } else {
      updated = [...saved, professional.id];
    }
    localStorage.setItem('planstack_shortlist', JSON.stringify(updated));
    setIsSaved(!isSaved);
  };

  return (
    <div className={`
      bg-white rounded-2xl overflow-hidden transition-all duration-300
      ${professional.tier === ListingTier.EXCLUSIVE ? 'border-2 border-accent-400 shadow-lg ring-4 ring-accent-400/5' : 'border border-gray-100'}
      hover:shadow-xl relative group
    `}>
      <button 
        onClick={toggleSave}
        className={`absolute top-4 right-4 z-20 p-2 rounded-full backdrop-blur-md transition-all ${isSaved ? 'bg-red-500 text-white scale-110' : 'bg-white/80 text-gray-400 hover:text-red-500 hover:scale-110 border border-gray-100'}`}
      >
        <Heart size={18} fill={isSaved ? 'currentColor' : 'none'} />
      </button>

      <div className="flex flex-col sm:flex-row">
        {/* Image Section */}
        {(isPremiumOrExclusive || isFeatured) && (
          <div className="sm:w-72 h-48 sm:h-auto relative overflow-hidden bg-gray-100 flex-shrink-0">
            {professional.imageUrl ? (
              <img 
                src={professional.imageUrl} 
                alt={professional.businessName} 
                className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500"
              />
            ) : (
               <div className="w-full h-full flex items-center justify-center text-gray-300">
                 {professional.logoUrl ? <img src={professional.logoUrl} className="w-1/2 object-contain" /> : <span className="text-4xl font-black">P</span>}
               </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
               {professional.tier === ListingTier.EXCLUSIVE && <span className="bg-accent-500 text-white text-[8px] px-2 py-0.5 rounded font-black uppercase">Elite Partner</span>}
               {professional.tier === ListingTier.PREMIUM && <span className="bg-brand-500 text-white text-[8px] px-2 py-0.5 rounded font-black uppercase">Premium</span>}
            </div>
          </div>
        )}

        {/* Content Section */}
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {professional.logoUrl && !isPremiumOrExclusive && (
                  <img src={professional.logoUrl} className="w-6 h-6 object-contain rounded" />
                )}
                <h3 className="text-xl font-black text-gray-900 leading-tight group-hover:text-brand-600 transition">
                  {professional.businessName}
                </h3>
                {professional.isVerified ? (
                  <span title="Verified Professional">
                    <ShieldCheck size={16} className="text-green-500" />
                  </span>
                ) : (
                  <span title="Verification Pending">
                    <ShieldAlert size={16} className="text-amber-500" />
                  </span>
                )}
              </div>
              <p className="text-xs font-bold text-brand-500 uppercase tracking-widest mt-0.5">{professional.category}</p>
            </div>
            <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-lg border border-gray-100">
               <Star size={14} className="text-yellow-400 fill-current" />
               <span className="text-xs font-black text-gray-800">{professional.rating}</span>
               <span className="text-[10px] text-gray-400">({professional.reviewCount})</span>
            </div>
          </div>

          <div className="flex items-center text-gray-400 text-xs font-bold mb-4 uppercase tracking-tighter">
            <MapPin size={12} className="mr-1 text-brand-600" />
            {professional.location.address && `${professional.location.address}, `}
            {professional.location.suburb}, {professional.location.state}
          </div>

          <p className="text-gray-500 text-sm mb-5 line-clamp-2 leading-relaxed">
            {professional.description}
          </p>

          {/* Premium Contact Block */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center text-gray-900 font-bold text-[10px] uppercase tracking-wider">
              <Phone size={12} className="mr-1.5 text-brand-600" />
              {professional.contact.phone}
            </div>
            
            {isPremiumOrExclusive && (
              <>
                {professional.contact.website && (
                  <a href={professional.contact.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-brand-600 font-bold text-[10px] uppercase tracking-wider hover:underline">
                    <Globe size={12} className="mr-1.5" />
                    Website
                  </a>
                )}
                <div className="flex items-center text-gray-600 font-bold text-[10px] uppercase tracking-wider">
                  <Mail size={12} className="mr-1.5 text-brand-600" />
                  {professional.contact.email}
                </div>
              </>
            )}
          </div>

          <div className="mt-auto flex items-center justify-between pt-5 border-t border-gray-50">
             <div className="flex flex-wrap gap-1.5">
               {professional.tags.slice(0, 2).map(tag => (
                 <span key={tag} className="text-[9px] bg-gray-50 text-gray-400 px-2 py-0.5 rounded font-bold uppercase">
                   {tag}
                 </span>
               ))}
             </div>
             
             <Link 
               to={`/professional/${professional.id}`}
               className="bg-brand-900 text-white text-xs font-black px-6 py-2.5 rounded-xl hover:bg-black transition shadow-sm uppercase tracking-widest"
             >
               Full Profile
             </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
